import { createContext, useContext, type ReactNode } from "react";

/**
 * The handful of words `packages/ui` has to say on its own.
 *
 * <p>Everything else in this package takes its text as a prop, because the package carries no
 * dictionary (US-37.6, US-37.7). These three are different only in that nobody passes them: the
 * close button of a dialog is rendered by `DialogContent` itself, and asking ninety-seven call
 * sites to hand it the word "Zamknij" would mean ninety-seven chances to forget. So the app says
 * it once, at the root, and the components read it here.
 */
export interface UiLabels {
  /** Accessible name of the × that closes a dialog or a sheet. */
  close: string;
  /** Accessible name of the previous-page button. */
  previous: string;
  /** Accessible name of the next-page button. */
  next: string;
  /** Accessible name of a table's wrapper when it scrolls sideways and so takes keyboard focus. */
  table: string;
  /**
   * The product's name for the browser tab ("Partie · BeanFlow"). Empty — the default — leaves
   * `document.title` alone, so an application that has not said its name keeps the one in its HTML.
   */
  productName: string;
}

const DEFAULTS: UiLabels = { close: "Close", previous: "Previous", next: "Next", table: "Table", productName: "" };

const UiLabelsContext = createContext<UiLabels>(DEFAULTS);

/**
 * Mount once per application, with the words from its own dictionary.
 *
 * <p>Without it the defaults above apply, which is English — deliberately, so that a component
 * rendered outside the provider (a test, a Storybook page) still has a name rather than nothing.
 */
export function UiLabelsProvider({ labels, children }: { labels: Partial<UiLabels>; children: ReactNode }) {
  // No `useMemo`: the React Compiler memoises this for us, and hand-written memoisation over a prop
  // object is exactly the pattern it reports as unpreservable (CLAUDE.md §7, rule 3).
  const value = { ...DEFAULTS, ...labels };
  return <UiLabelsContext.Provider value={value}>{children}</UiLabelsContext.Provider>;
}

export function useUiLabels(): UiLabels {
  return useContext(UiLabelsContext);
}
